/* eslint-disable react-hooks/exhaustive-deps */
import { literals } from "@/constants/literals";
import { useEffect, useMemo, useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import useGarageStore from "@/store/useGarbage.store";
import { useNavigate, useParams } from "react-router-dom";
import type { CapturedPhoto } from "@/types/types.d";
import { useDevice } from "@/store/useDevice";

import Textarea from "@/components/textarea/Textarea";
import InputCheckbox from "@/components/input-checkbox/InputCheckbox";
import { CameraCapture } from "../../components/CameraCapture";
import { BASE_URL, NACELLE_CONFIGURATION } from "@/constants/constants";
import OrderHeader from "../components/OrderHeader";
import NacelleFinfingsInputSearchSelect from "@/components/input-search-select/NacelleFinfingsInputSearchSelect";

interface FormData {
  description: string;
  visibleForClient: boolean;
  photos: CapturedPhoto[];
}

const initial_status = {
  description: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const EditionGroupPage = () => {
  const { groupId } = useParams() as { groupId: string };
  const { garageData, garageGroupData, updateGarageGroupData } =
    useGarageStore();
  const [formData, setFormData] = useState<FormData>(initial_status);
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const isNacelleFindings = useMemo(
    () =>
      garageData?.step === NACELLE_CONFIGURATION.FINDINGS &&
      garageData.garage === NACELLE_CONFIGURATION.GARAGE_ID,
    [garageData],
  );

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setFormData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const isValid = () => !isNacelleFindings || formData.description.length > 0;

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    updateGarageGroupData({ ...formData, id: Number(groupId) });
    navigate(`${BASE_URL}/capture/garage/resume`);
  };

  useEffect(() => {
    const groupData = garageGroupData.find(
      (group) => group.id.toString() === groupId,
    );
    if (groupData) {
      setFormData({
        description: groupData.description,
        visibleForClient: groupData.visibleForClient,
        photos: groupData.photos,
      });
    }
  }, []);

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <OrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={formData?.photos || undefined}
            />
          </div>
          <div className="w-full my-4 space-y-4 md:flex md:flex-col-reverse md:gap-y-4">
            {isNacelleFindings ? (
              <div className="md:max-w-lg w-full">
                <NacelleFinfingsInputSearchSelect
                  label={literals.description}
                  id={"description"}
                  name="description"
                  value={formData.description}
                  size={"medium"}
                  onChange={(new_value) =>
                    handleOnChange("description", new_value)
                  }
                />
              </div>
            ) : (
              <Textarea
                label={literals.description}
                onChange={(new_value) =>
                  handleOnChange("description", new_value)
                }
                name="description"
                value={formData.description}
                deleteMessage={literals.cleanDescription}
                size="medium"
                rows={isMobile ? 1 : 5}
              />
            )}
            <InputCheckbox
              title={literals.visibleForClients}
              onChange={(new_value) =>
                handleOnChange("visibleForClient", new_value)
              }
              checked={formData.visibleForClient}
              id="visibleForClient"
            />
          </div>
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.saveChanges}
              colour="red"
              disabled={!isValid()}
              type="submit"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditionGroupPage;
